import React, { useState, useEffect } from "react";
import { Stack, Box } from "@mui/material";
import {
  postThread,
  createTask,
  getScheduledPosts,
  updateScheduledPost,
  deleteScheduledPost,
  // 새로운 Firestore 기반 함수들
  createFirestoreTask,
  deleteFirestoreTask,
  // Phase B: 관리 기능
  updateFirestoreTask,
} from "../utils/taskDataFetch";
// getMyCollections import 제거 - useCollections 훅 사용
import { isTextOverLimit, MAX_TEXT_LENGTH } from "../utils/textColorUtils";
import { useActiveTasksSubscription } from "../hooks/useTaskSubscription";
import { useUser } from "../contexts/UserContext";
import { useCollections } from "../contexts/ThreadsContext";
import { useWritingMode } from "../contexts/WritingModeContext";
// useIsMobile hook 제거 - MUI sx로 반응형 처리
import { PageContainer } from "../components/Container/PageContainer";
import ThreadComposer from "../components/PostThreads/ThreadComposer";
import ScheduleSection from "../components/PostThreads/ScheduleSection";
import { ButtonBasic } from "../components/button/ButtonBasic";
import FullScreenEditor from "../components/PostThreads/FullScreenEditor";
import NotificationSection from "../components/PostThreads/NotificationSection";
import ScheduledPostsList from "../components/PostThreads/ScheduledPostsList";
import EditPostModal from "../components/PostThreads/EditPostModal";
import CollectionSelector from "../components/PostThreads/CollectionSelector";

const PostThreads = () => {
  const { profile } = useUser();

  // WritingMode Context 사용
  const { writingMode, switchMode, isFocusMode } = useWritingMode();

  // useIsMobile hook 제거 - MUI sx로 반응형 처리

  // 새로운 threads 기반 상태
  const [threads, setThreads] = useState([{ id: 1, text: "", isMain: true }]);
  // threadHelpers 삭제 - 더 이상 필요 없음

  // 기존 상태들 (호환성 유지)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isScheduled, setIsScheduled] = useState(true); // 예약모드 디폴트
  const [scheduleDateTime, setScheduleDateTime] = useState(new Date());
  const [useFirestore, setUseFirestore] = useState(true); // Firestore 기반 사용 여부

  // 컬렉션 관련 상태 - useCollections 훅 사용
  const { collections: myCollections, loadCollections } = useCollections();
  const [collectionConfig, setCollectionConfig] = useState({
    mode: "none",
    autoAddToCollection: false,
  });

  // 사용자 ID
  const userID = localStorage.getItem("userID");

  // Firestore 실시간 구독 (새로운 방식)
  const {
    tasks: firestoreTasks,
    loading: firestoreLoading,
    error: firestoreError,
  } = useActiveTasksSubscription(userID);

  // ThreadComposer 변경 핸들러 - 삭제 (더 이상 helpers 필요 없음)

  // 500자 초과 체크
  const hasOverLimitText = threads.some((thread) =>
    isTextOverLimit(thread.text, MAX_TEXT_LENGTH)
  );

  // 메인 스레드가 비어있는지 체크
  const mainThread = threads.find((t) => t.isMain);
  const hasNoContent = !mainThread?.text.trim();

  const isPostDisabled = loading || hasOverLimitText || hasNoContent;

  // 데이터 변환 헬퍼 함수들
  const convertThreadsToLegacyFormat = (threadsData) => {
    const mainThread = threadsData.find((t) => t.isMain);
    const replyThreads = threadsData.filter((t) => !t.isMain && t.text.trim());

    return {
      text: mainThread?.text || "",
      replies: replyThreads.map((t) => t.text),
    };
  };

  // 기존 HTTP API 기반 상태 (호환성 유지)
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);

  // 모달 관련 상태
  const [selectedPost, setSelectedPost] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editThreads, setEditThreads] = useState([
    { id: 1, text: "", isMain: true },
  ]); // 편집용 threads
  const [editDateTime, setEditDateTime] = useState(new Date());

  // 기존 편집 상태들 (호환성 유지)

  // 컬렉션 데이터 로드
  useEffect(() => {
    const userID = profile?.id || localStorage.getItem("userID");
    if (userID) {
      loadCollections(userID);
    }
  }, [profile?.id, loadCollections]);

  // 기존 HTTP API 기반 예약글 목록 조회 (Firestore 비활성화 시)
  useEffect(() => {
    if (!useFirestore) {
      const fetchScheduledPosts = async () => {
        try {
          setLoadingPosts(true);
          const posts = await getScheduledPosts();
          console.log("HTTP API posts", posts);
          setScheduledPosts(posts);
        } catch (err) {
          console.error("예약글 목록 조회 실패:", err);
          setError("예약글 목록을 불러오는데 실패했습니다.");
        } finally {
          setLoadingPosts(false);
        }
      };

      fetchScheduledPosts();
    }
  }, [useFirestore]);

  // Firestore 에러 처리
  useEffect(() => {
    if (firestoreError) {
      console.error("Firestore 실시간 구독 오류:", firestoreError);
      setError(`실시간 업데이트 오류: ${firestoreError}`);
    }
  }, [firestoreError]);

  const handlePost = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    console.log("handlePost 호출");
    try {
      // 새로운 threads 상태에서 데이터 추출
      const { text, replies } = convertThreadsToLegacyFormat(threads);

      if (!text.trim()) {
        throw new Error("스레드 내용을 입력해주세요.");
      }

      // 댓글 내용 검증 (빈 댓글이 있는지 확인)
      const emptyReplyIndex = replies.findIndex((reply) => !reply.trim());
      if (emptyReplyIndex !== -1) {
        throw new Error(`${emptyReplyIndex + 1}번째 댓글 내용을 입력해주세요.`);
      }

      // 실제 사용할 댓글 텍스트 배열
      const finalReplies = replies;

      if (isScheduled) {
        // 예약 포스트 처리
        if (!scheduleDateTime) {
          throw new Error("날짜와 시간을 선택해주세요.");
        }

        // 날짜와 시간 조합하여 KST 형식으로 변환
        const year = scheduleDateTime.getFullYear();
        const month = String(scheduleDateTime.getMonth() + 1).padStart(2, "0");
        const day = String(scheduleDateTime.getDate()).padStart(2, "0");
        const hours = String(scheduleDateTime.getHours()).padStart(2, "0");
        const minutes = String(scheduleDateTime.getMinutes()).padStart(2, "0");
        const seconds = "00";

        const executeAt = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

        let response;

        if (useFirestore) {
          // 🔥 새로운 Firestore 기반 예약 생성 (컬렉션 정보 포함)
          console.log("Creating Firestore task...", { collectionConfig });
          const taskData = {
            text: text,
            replies: finalReplies,
            executeAt: executeAt,
            autoAddToCollection: collectionConfig.autoAddToCollection || false,
          };

          // 컬렉션 설정 추가
          if (
            collectionConfig.mode === "existing" &&
            collectionConfig.collectionId
          ) {
            taskData.collectionId = collectionConfig.collectionId;
          } else if (
            collectionConfig.mode === "new" &&
            collectionConfig.newCollectionTitle
          ) {
            // 새 컬렉션 생성 정보 추가
            taskData.createNewCollection = {
              title: collectionConfig.newCollectionTitle,
              description: collectionConfig.newCollectionDescription || "",
            };
          }

          response = await createFirestoreTask(taskData);
          console.log("Firestore task response", response);
          // Firestore는 실시간 구독으로 자동 업데이트됨
        } else {
          // 기존 HTTP API 기반 예약 생성
          console.log("Creating HTTP API task...");
          response = await createTask({
            text: text,
            replies: finalReplies,
            executeAt: executeAt,
          });
          console.log("HTTP API task response", response);

          // HTTP API는 수동으로 목록 새로고침
          const posts = await getScheduledPosts();
          setScheduledPosts(posts);
        }

        setSuccess(
          "스레드가 예약되었습니다. 예약된 시간에 자동으로 게시됩니다."
        );
      } else {
        // 일반 포스트 처리 - 즉시 게시
        const response = await postThread(text, finalReplies);
        console.log("postThread response", response);
        setSuccess("스레드가 성공적으로 게시되었습니다.");
      }

      // 입력값 초기화
      setThreads([{ id: 1, text: "", isMain: true }]);
      setScheduleDateTime(new Date());
      setCollectionConfig({ mode: "none", autoAddToCollection: false });
    } catch (err) {
      // 오류 유형 구분
      if (err.response) {
        // 서버에서 응답이 왔지만 오류 상태 코드인 경우
        const statusCode = err.response.status;
        if (statusCode === 401 || statusCode === 403) {
          setError("권한이 없거나 인증이 만료되었습니다. 다시 로그인해주세요.");
        } else if (statusCode === 429) {
          setError("요청이 너무 많습니다. 잠시 후 다시 시도해주세요.");
        } else if (statusCode >= 500) {
          setError("서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
        } else {
          setError(
            `요청 처리 중 오류 발생 (${statusCode}): ${
              err.response.data?.message || err.message
            }`
          );
        }
      } else if (err.request) {
        // 요청은 성공했지만 응답을 받지 못한 경우
        setError("서버 응답이 없습니다. 네트워크 연결을 확인해주세요.");
      } else {
        // 요청 생성 자체가 실패한 경우
        setError(
          `요청 생성 실패: ${err.message || "스레드 게시에 실패했습니다."}`
        );
      }

      // 오류 로깅
      console.error("API 호출 실패:", {
        errorType: err.name,
        message: err.message,
        stack: err.stack,
        isScheduled: isScheduled,
      });
    } finally {
      setLoading(false);
    }
  };

  // 예약글 수정 모달 열기
  const openEditModal = (post) => {
    setSelectedPost(post);

    // 새로운 threads 형태로 변환
    const threads = [{ id: 1, text: post.text, isMain: true }];
    if (post.replies && Array.isArray(post.replies)) {
      post.replies.forEach((reply, index) => {
        threads.push({
          id: index + 2,
          text: reply,
          isMain: false,
        });
      });
    } else if (post.reply) {
      // 이전 버전 호환성 유지
      threads.push({ id: 2, text: post.reply, isMain: false });
    }

    setEditThreads(threads);

    setEditDateTime(new Date(post.executeAt));
    setIsEditModalOpen(true);
  };

  // 예약글 수정 저장
  const handleSaveEdit = async () => {
    try {
      setLoading(true);

      // 새로운 editThreads 상태에서 데이터 추출
      const { text: editText, replies: editReplies } =
        convertThreadsToLegacyFormat(editThreads);

      // 댓글 내용 검증 (빈 댓글이 있는지 확인)
      const emptyReplyIndex = editReplies.findIndex((reply) => !reply.trim());
      if (emptyReplyIndex !== -1) {
        throw new Error(`${emptyReplyIndex + 1}번째 댓글 내용을 입력해주세요.`);
      }

      // 실제 사용할 댓글 텍스트 배열
      const finalReplies = editReplies;

      // 날짜와 시간 조합하여 KST 형식으로 변환
      const year = editDateTime.getFullYear();
      const month = String(editDateTime.getMonth() + 1).padStart(2, "0");
      const day = String(editDateTime.getDate()).padStart(2, "0");
      const hours = String(editDateTime.getHours()).padStart(2, "0");
      const minutes = String(editDateTime.getMinutes()).padStart(2, "0");
      const seconds = "00";

      const executeAt = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

      const taskId = selectedPost.id;

      if (useFirestore) {
        // 🔥 새로운 Firestore 기반 수정 (삭제→재생성)
        console.log("Updating Firestore task...");
        await updateFirestoreTask(taskId, {
          text: editText,
          executeAt: executeAt,
          replies: finalReplies,
        });
        // Firestore는 실시간 구독으로 자동 업데이트됨
      } else {
        // 기존 HTTP API 기반 수정
        console.log("Updating HTTP API task...");
        await updateScheduledPost(taskId, {
          text: editText,
          replies: finalReplies,
          executeAt: executeAt,
        });

        // HTTP API는 수동으로 목록 새로고침
        const posts = await getScheduledPosts();
        setScheduledPosts(posts);
      }
      setIsEditModalOpen(false);
      setSuccess("예약글이 수정되었습니다.");
    } catch (err) {
      console.error("예약글 수정 실패:", err);
      setError(typeof err === "string" ? err : "예약글 수정에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 예약글 삭제
  const handleDeletePost = async () => {
    if (!selectedPost) return;

    const confirmDelete = window.confirm("이 예약글을 삭제하시겠습니까?");
    if (!confirmDelete) return;

    try {
      setLoading(true);

      if (useFirestore) {
        // 🔥 새로운 Firestore 기반 삭제
        console.log("Deleting Firestore task...");
        await deleteFirestoreTask(selectedPost.id);
        console.log("Firestore task deleted");
        // Firestore는 실시간 구독으로 자동 업데이트됨
      } else {
        // 기존 HTTP API 기반 삭제
        console.log("Deleting HTTP API task...");
        await deleteScheduledPost(selectedPost.id);
        console.log("HTTP API task deleted");

        // HTTP API는 수동으로 목록 새로고침
        const posts = await getScheduledPosts();
        setScheduledPosts(posts);
      }

      setIsEditModalOpen(false);
      setSuccess("예약글이 삭제되었습니다.");
    } catch (err) {
      console.error("예약글 삭제 실패:", err);
      setError("예약글 삭제에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Focus 모드일 때 전체화면 렌더링 */}
      {isFocusMode && (
        <Box
          sx={{
            position: "fixed",
            top: 64,
            left: 0,
            width: "100vw",
            height: "calc(100vh - 64px)",
            zIndex: 100,
            backgroundColor: "background.default",
          }}
        >
          <FullScreenEditor
            threads={threads}
            onThreadsChange={setThreads}
            loading={loading}
            isScheduled={isScheduled}
            setIsScheduled={setIsScheduled}
            scheduleDateTime={scheduleDateTime}
            setScheduleDateTime={setScheduleDateTime}
            collectionConfig={collectionConfig}
            setCollectionConfig={setCollectionConfig}
            myCollections={myCollections}
            handlePost={handlePost}
            isPostDisabled={isPostDisabled}
            hasOverLimitText={hasOverLimitText}
            MAX_TEXT_LENGTH={MAX_TEXT_LENGTH}
          />

          <NotificationSection
            error={error}
            success={success}
            setError={setError}
            setSuccess={setSuccess}
          />

          <EditPostModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            editThreads={editThreads}
            setEditThreads={setEditThreads}
            editDateTime={editDateTime}
            setEditDateTime={setEditDateTime}
            loading={loading}
            onSave={handleSaveEdit}
            onDelete={handleDeletePost}
          />
        </Box>
      )}
        
      {/* Preview 모드 - PageContainer로 통합 관리 - 항상 렌더링 */}
      <Box sx={{ display: isFocusMode ? 'none' : 'block' }}>
        <PageContainer>
          <Box
            sx={{
              // 모바일에서 ThreadComposer만 화면 전체 기준 중앙 정렬
              width: {
                xs: "100vw", // 모바일: 전체 화면 너비
                xl: "100%", // PC: PageContainer 너비
              },
              maxWidth: "384px",
              margin: "0 auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: "384px",
                marginLeft: {
                  xs: 0,
                  sm: "-256px",
                },
              }}
            >
              <ThreadComposer
                threads={threads}
                onThreadsChange={setThreads}
                loading={loading}
                maxThreads={5}
              />
              <Box sx={{ mt: 3 }}>
                <ScheduleSection
                  isScheduled={isScheduled}
                  setIsScheduled={setIsScheduled}
                  scheduleDateTime={scheduleDateTime}
                  setScheduleDateTime={setScheduleDateTime}
                  loading={loading}
                  hideToggle={true}
                />
              </Box>

              {/* 컬렉션 선택 UI */}
              {isScheduled && (
                <Box sx={{ mt: 3 }}>
                  <CollectionSelector
                    value={collectionConfig}
                    onChange={setCollectionConfig}
                    collections={myCollections}
                    loading={loading}
                    disabled={loading}
                  />
                </Box>
              )}

              <Box sx={{ mt: 2 }}>
                <ButtonBasic
                  onClick={handlePost}
                  loading={loading}
                  disabled={isPostDisabled}
                  size="xl"
                  widthType="full"
                  actionType="primary"
                  radius="full"
                >
                  {isScheduled ? "예약 게시하기" : "게시하기"}
                </ButtonBasic>

                {hasOverLimitText && (
                  <Box
                    sx={{
                      fontSize: "12px",
                      color: "error.main",
                      mt: 1,
                      textAlign: "center",
                    }}
                  >
                    일부 텍스트가 {MAX_TEXT_LENGTH}자를 초과했습니다. 내용을
                    줄여주세요.
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          <NotificationSection
            error={error}
            success={success}
            setError={setError}
            setSuccess={setSuccess}
          />

          <ScheduledPostsList
            scheduledPosts={useFirestore ? firestoreTasks : scheduledPosts}
            loadingPosts={useFirestore ? firestoreLoading : loadingPosts}
            openEditModal={openEditModal}
          />

          <EditPostModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            editThreads={editThreads}
            setEditThreads={setEditThreads}
            editDateTime={editDateTime}
            setEditDateTime={setEditDateTime}
            loading={loading}
            onSave={handleSaveEdit}
            onDelete={handleDeletePost}
          />
        </PageContainer>
      </Box>
    </>
  );
};

export default PostThreads;
